import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, firstValueFrom } from 'rxjs'

import { HarnessConfigService } from '@flowx/designer/modules/ai-builder/services/harness-config.service'
import {
  HarnessUiContext,
  HarnessUiContextService,
} from '@flowx/designer/modules/ai-builder/services/harness-ui-context.service'
import {
  HarnessAttachmentRef,
  HarnessAttachmentsPolicy,
} from '@flowx/designer/modules/ai-builder/types/conversation-file.types'
import {
  HarnessConversationPage,
  HarnessConversationQuery,
  HarnessDeletionPreview,
  toConversationPage,
  toDeletionPreview,
} from '@flowx/designer/modules/ai-builder/types/conversation.types'
import {
  DEFAULT_OPERATION_ID,
  HARNESS_ERROR_CODES,
  HarnessCancelOutcome,
  HarnessDecision,
  HarnessExecutionPreview,
  HarnessExecutionTarget,
  HarnessFeedbackRequest,
  HarnessFrame,
  HarnessItemPreview,
  HarnessMergeAnswer,
  HarnessMessageFeedback,
  HarnessSessionHandle,
  HarnessSessionRecord,
  HarnessSessionSnapshot,
  HarnessSteering,
  MAX_WORK_ITEMS,
  toCancelOutcome,
  toExecutionPreview,
  toItemPreview,
  toMessageFeedback,
  toSessionHandle,
  toSessionRecord,
  toSessionSnapshot,
} from '@flowx/designer/modules/ai-builder/types/harness.types'
import { HarnessCallScope } from '@flowx/designer/modules/ai-builder/utils/call-scope.util'
import { toFrame } from '@flowx/designer/modules/ai-builder/utils/harness-frames.util'
import { asString, isRecord } from '@flowx/designer/modules/ai-builder/utils/payload.util'
import { RequestScope, requestScopeContext } from '@flowx/designer/shared/api/request-scope.token'
import { SESSION_LOGOUT } from '@flowx/designer/shared/api/session-logout.token'
import { SseFrameParser } from '@flowx/designer/shared/api/sse-frame-parser'

export type HarnessMessageContext = {
  headers: Record<string, string>
  uiContext?: HarnessUiContext
  /** The project the call acts on, with the page's context alongside it (doc 37 N1). */
  call: HarnessCallScope
}

/** What confirming an amendment carries besides the preview it confirms. */
export type HarnessAmendmentConfirmation = {
  planVersion: number
  previewId: string
  operationIds: readonly string[]
  /** The amendment preview's policy or commit review id, when it had one to confirm. */
  confirmedReviewId?: string
  /** "Let the agent adjust within this plan" for the amended version (D15). */
  allowAdjustments: boolean
}

/** A call scope as the interceptors read it. */
function projectRequest(call: HarnessCallScope): RequestScope {
  return { type: 'project', workspaceId: call.workspaceId, appId: call.appId }
}

/** An owner route: the maker and the organization only, no project. */
const OWNER: RequestScope = { type: 'owner' }

/**
 * The harness transport.
 *
 * Two transports, chosen by need rather than by feature. The reads go through `HttpClient`, so
 * they inherit the interceptor chain every other designer call has: the bearer, `Fx-Workspace-Id`
 * and `Fx-AppId` stamped for us, a 303 turned into the workspace-select redirect, and — the one
 * that matters — a 401 ending the designer session instead of leaving the maker in a dead one.
 *
 * The two streaming calls cannot: `HttpClient` has no way to expose a response body incrementally,
 * and both are POST-with-body, which also rules out `EventSource` (GET only, no body). They pay
 * for that with headers written by hand in `HarnessConfigService.headersFor()` and a 401 that has to
 * call `logout()` itself, because nothing else will.
 *
 * Deliberately no automatic reconnect or backoff, unlike `events-gateway-sse.service.ts`: a turn
 * runs detached from its request, so the store reattaches explicitly (`streamTurnTail`) once it
 * has read where the session stands.
 */
@Injectable({ providedIn: 'root' })
export class HarnessApi {
  readonly #config = inject(HarnessConfigService)
  readonly #http = inject(HttpClient)
  readonly #logout = inject(SESSION_LOGOUT)
  readonly #uiContext = inject(HarnessUiContextService)

  /** The scope a turn or resume is sent in, captured at the press. The headers name the call's
   * project, and the page's own context rides along. */
  captureMessageContext(call: HarnessCallScope): HarnessMessageContext {
    return {
      headers: this.#config.headersFor(call.workspaceId, call.appId),
      uiContext: this.#uiContext.snapshot({
        workspaceId: call.workspaceId,
        appId: call.appId,
        appVersionId: call.context.app_version_id,
      }),
      call,
    }
  }

  async openSession(context: HarnessMessageContext): Promise<HarnessSessionHandle> {
    return this.#read(
      this.#http.post(
        `${this.#config.baseUrl()}/sessions`,
        { context: context.call.context },
        this.#options(projectRequest(context.call))
      ),
      toSessionHandle
    )
  }

  /** The maker's own conversations across the organization, newest first (D5). Keyset-paged:
   * `cursor` is the previous page's `next_cursor`. */
  async listConversations(query: HarnessConversationQuery): Promise<HarnessConversationPage> {
    const params = new URLSearchParams(
      Object.entries({ limit: CONVERSATION_PAGE, ...query })
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)])
    )

    return this.#read(
      this.#http.get(`${this.#config.baseUrl()}/conversations?${params}`, this.#options(OWNER)),
      toConversationPage
    )
  }

  /** The attachments policy the conversation list carries, so the composer knows it before any
   * conversation is open. Null from a harness that publishes none. */
  async readAttachmentsPolicy(): Promise<HarnessAttachmentsPolicy | null> {
    const page = await this.listConversations({ filter: 'all', limit: 1 })

    return page.attachmentsPolicy ?? null
  }

  /** One of the maker's conversations, read as its owner: no project, no version, and the
   * conversation's own fields — target, plans, grants, where it stands — beside the record. */
  async readConversation(sessionId: string): Promise<HarnessSessionSnapshot> {
    return this.#read(
      this.#http.get(this.#sessionPath(sessionId), this.#options(OWNER)),
      toSessionSnapshot
    )
  }

  /** What deleting the conversation would remove, what it cannot, and whether it can go now. */
  async deletionPreview(sessionId: string): Promise<HarnessDeletionPreview> {
    return this.#read(
      this.#http.get(`${this.#sessionPath(sessionId)}/deletion-preview`, this.#options(OWNER)),
      toDeletionPreview
    )
  }

  /** Delete the conversation from every harness-side store. Refused (409) while a turn or a run is
   * live; a second delete of the same id is a 404. */
  async deleteConversation(sessionId: string): Promise<void> {
    await this.#read(
      this.#http.delete(this.#sessionPath(sessionId), this.#options(OWNER)),
      (value) => (isRecord(value) ? value : null)
    )
  }

  /** One plan of the conversation as data — versions, approvals, runs and commits — to keep before
   * deleting it. Returned as the harness sent it: it is written to a file, not read. */
  async exportPlan(sessionId: string, planId: string): Promise<Record<string, unknown>> {
    return this.#read(
      this.#http.get(
        `${this.#sessionPath(sessionId)}/plans/${encodeURIComponent(planId)}/export`,
        this.#options(OWNER)
      ),
      (value) => (isRecord(value) ? value : null)
    )
  }

  /** Revoke a read grant: no later turn reads under it, and what it read is dropped. */
  async revokeGrant(sessionId: string, grantId: string): Promise<void> {
    await this.#read(
      this.#http.delete(
        `${this.#sessionPath(sessionId)}/grants/${encodeURIComponent(grantId)}`,
        this.#options(OWNER)
      ),
      (value) => (isRecord(value) ? value : null)
    )
  }

  /** Close a plan while keeping its history. `expected_revision` is the record as the maker last
   * saw it: the harness refuses (409) an abandon against a record that has moved on, or one whose
   * turn is still running. */
  async abandonPlan(
    sessionId: string,
    expectedRevision: number,
    call: HarnessCallScope
  ): Promise<HarnessSessionRecord> {
    return this.#read(
      this.#http.post(
        `${this.#config.baseUrl()}/sessions/${encodeURIComponent(sessionId)}/abandon`,
        {
          ...call.body,
          expected_revision: expectedRevision,
        },
        this.#options(projectRequest(call))
      ),
      toSessionRecord
    )
  }

  /** Ask the harness to stop the running turn; only `stopped` means it ended. */
  async cancelTurn(sessionId: string, call?: HarnessCallScope): Promise<HarnessCancelOutcome> {
    const query = call ? new URLSearchParams(call.body) : this.#sessionQuery()

    return this.#read(
      this.#http.post(
        `${this.#config.baseUrl()}/sessions/${encodeURIComponent(sessionId)}/turn/cancel?${query}`,
        null,
        this.#options(call && projectRequest(call))
      ),
      toCancelOutcome
    )
  }

  /**
   * Prepare one work item for a run: the harness reads the committed baseline, checks the item's
   * typed intent against its registry, and answers with what the run would do — names, values,
   * the summary — plus a `preview_id` the run must echo back. No write happens here. A 422 is the
   * harness saying the item is not one it can run, and its `detail` is the sentence to show.
   */
  async previewItem(
    sessionId: string,
    itemId: string,
    planVersion: number,
    operationId: string,
    executionTarget: HarnessExecutionTarget | undefined,
    call: HarnessCallScope
  ): Promise<HarnessItemPreview> {
    return this.#read(
      this.#http.post(
        `${this.#config.baseUrl()}/sessions/${encodeURIComponent(sessionId)}/${executionPath(operationId)}/preview`,
        {
          ...call.body,
          item_id: itemId,
          expected_plan_version: planVersion,
          ...(executionTarget && { execution_target: executionTarget }),
        },
        this.#options(projectRequest(call))
      ),
      toItemPreview
    )
  }

  /**
   * Run a previewed item, following it as a stream. The harness runs it on a worker of its own —
   * this stream is a window onto it, not its lifetime: `lifecycle` snapshots as the run moves
   * (queued → running → committed), then `branch_ready`, then `end`. Calling again for a run that
   * exists reconnects to it (or retries a failed one) rather than starting a second; the
   * `preview_id` is what binds the call to the one run. This is not observation: a paused plan
   * resumes, and omitting stop_after_story clears its stored boundary. Story callers must supply
   * their explicit/live-session intent; observer reconnects use the owner read instead.
   */
  streamRun(
    sessionId: string,
    preview: HarnessExecutionPreview,
    confirmedCommitReviewId: string | undefined,
    maxItems = MAX_WORK_ITEMS,
    options: {
      call: HarnessCallScope
      continueRenderUnverified?: boolean
      stopAfterStory?: boolean
      /** A plan run's operations, resolved by the caller. */
      operationIds?: readonly string[]
    }
  ): Observable<HarnessFrame> {
    const path = preview.type === 'plan' ? PLAN_EXECUTION_PATH : executionPath(preview.operation_id)
    const { call, continueRenderUnverified, stopAfterStory, operationIds = [] } = options
    return this.#stream(
      `/sessions/${encodeURIComponent(sessionId)}/${path}/run`,
      {
        ...call.body,
        ...(preview.type === 'plan'
          ? {
              operation_ids: [...new Set(operationIds)],
              max_items: maxItems,
              ...(stopAfterStory !== undefined && { stop_after_story: stopAfterStory }),
              // The maker's answer to `render_unverified_offer`; only a plan run takes it.
              ...(continueRenderUnverified && { continue_render_unverified: true }),
            }
          : { item_id: preview.item_id }),
        expected_plan_version: preview.plan_version,
        preview_id: preview.preview_id,
        ...(confirmedCommitReviewId && { confirmed_commit_review_id: confirmedCommitReviewId }),
      },
      this.#config.headersFor(call.workspaceId, call.appId)
    )
  }

  async previewPlan(
    sessionId: string,
    planVersion: number,
    operationIds: readonly string[],
    executionTarget: HarnessExecutionTarget | undefined,
    call: HarnessCallScope
  ): Promise<HarnessExecutionPreview> {
    return this.#read(
      this.#http.post(
        `${this.#config.baseUrl()}/sessions/${encodeURIComponent(sessionId)}/${PLAN_EXECUTION_PATH}/preview`,
        {
          ...call.body,
          expected_plan_version: planVersion,
          operation_ids: [...new Set(operationIds)],
          ...(executionTarget && { execution_target: executionTarget }),
        },
        this.#options(projectRequest(call))
      ),
      toExecutionPreview
    )
  }

  async pausePlan(
    sessionId: string,
    runId: string,
    call: HarnessCallScope
  ): Promise<HarnessSessionRecord> {
    return this.#read(
      this.#http.post(
        `${this.#config.baseUrl()}/sessions/${encodeURIComponent(sessionId)}/${PLAN_EXECUTION_PATH}/pause`,
        { ...call.body, run_id: runId },
        this.#options(projectRequest(call))
      ),
      toSessionRecord
    )
  }

  /**
   * Review the change a turn parked while the plan ran — or the one a reassessment proposed (L2,
   * L3) — as a diff against the plan's remaining items (conversation-owned only). No items are sent:
   * the harness builds the preview from the plan's own parked proposal. `operationIds` covers the
   * completed prefix and the proposal, as the run that follows needs.
   */
  async previewAmendment(
    sessionId: string,
    planVersion: number,
    operationIds: readonly string[],
    call: HarnessCallScope
  ): Promise<HarnessExecutionPreview> {
    return this.#read(
      this.#http.post(
        `${this.#config.baseUrl()}/sessions/${encodeURIComponent(sessionId)}/${PLAN_EXECUTION_PATH}/amend/preview`,
        {
          ...call.body,
          expected_plan_version: planVersion,
          operation_ids: [...new Set(operationIds)],
        },
        this.#options(projectRequest(call))
      ),
      toExecutionPreview
    )
  }

  /** Confirm a previewed amendment. The plan gets a new version and the run waits at its item
   * boundary; the next Run continues it. Answers the session record. */
  async confirmAmendment(
    sessionId: string,
    confirmation: HarnessAmendmentConfirmation,
    call: HarnessCallScope
  ): Promise<HarnessSessionRecord> {
    return this.#read(
      this.#http.post(
        `${this.#config.baseUrl()}/sessions/${encodeURIComponent(sessionId)}/${PLAN_EXECUTION_PATH}/amend/confirm`,
        {
          ...call.body,
          expected_plan_version: confirmation.planVersion,
          operation_ids: [...new Set(confirmation.operationIds)],
          preview_id: confirmation.previewId,
          ...(confirmation.confirmedReviewId && {
            confirmed_commit_review_id: confirmation.confirmedReviewId,
          }),
          approval_options: { allow_adjustments: confirmation.allowAdjustments },
        },
        this.#options(projectRequest(call))
      ),
      toSessionRecord
    )
  }

  /**
   * Record or change the maker's feedback on one agent message. An upsert, keyed harness-side by
   * (session, message, maker), so changing a verdict is the same call as taking one.
   *
   * `messageId` comes from the session's own events; a message the harness cannot find, or one of
   * the maker's own turns, is refused (404 / 422) rather than filed against nothing.
   */
  async putMessageFeedback(
    sessionId: string,
    messageId: string,
    feedback: HarnessFeedbackRequest
  ): Promise<HarnessMessageFeedback> {
    return this.#read(
      this.#http.put(this.#feedbackPath(sessionId, messageId), {
        ...this.#config.sessionBody(),
        verdict: feedback.verdict,
        reasons: feedback.reasons,
        comment: feedback.comment,
        client_context: feedback.clientContext,
      }),
      toMessageFeedback
    )
  }

  /** Clear the maker's feedback. A 404 means there was nothing on record — which is the state the
   * caller wanted, so it is not an error worth surfacing. */
  async deleteMessageFeedback(sessionId: string, messageId: string): Promise<void> {
    await firstValueFrom(
      this.#http.delete(`${this.#feedbackPath(sessionId, messageId)}?${this.#sessionQuery()}`)
    )
  }

  /** `onAdmitted` is called once the harness has answered 2xx, before the first frame is read:
   * the turn is the harness's from then on. A refusal or a failed connection never calls it. */
  streamTurn(
    sessionId: string,
    prompt: string,
    context: HarnessMessageContext,
    attachments: readonly HarnessAttachmentRef[] = [],
    onAdmitted?: () => void
  ): Observable<HarnessFrame> {
    return this.#stream(
      `/sessions/${encodeURIComponent(sessionId)}/turns`,
      {
        prompt,
        ...(attachments.length && { attachments }),
        context: turnContextBody(context.call, context.uiContext),
      },
      context.headers,
      onAdmitted
    )
  }

  /**
   * Reattach to the session's latest turn: its frames from index `since`, then live ones, then
   * `lifecycle` and `end`. With the turn's buffer on the pod that answers, the replay is
   * frame-exact; otherwise (another pod, or the turn already over) it is rebuilt from the stored
   * transcript — tokens coalesced, `since` ignored — so callers that cannot tell the two apart pass
   * 0 and rebuild from the turn's start. A GET, unlike the other two streams.
   */
  streamTurnTail(sessionId: string, since = 0, call: HarnessCallScope): Observable<HarnessFrame> {
    const query = new URLSearchParams(call.body)
    query.set('since', String(since))

    return this.#stream(
      `/sessions/${encodeURIComponent(sessionId)}/turn/stream?${query}`,
      undefined,
      this.#config.headersFor(call.workspaceId, call.appId)
    )
  }

  /**
   * Answer an outstanding interrupt.
   *
   * `expectedInterruptId` names the interrupt being answered. The harness requires it on every session
   * it indexed — an answer without one, or one naming an interrupt since answered elsewhere, is refused
   * (409) before anything runs — so a screen two tabs behind cannot decide for the current one.
   *
   * `onAdmitted` is the same seam as on `streamTurn`.
   *
   * `message` carries prose for `respond`. `steering` carries a brief's structured changes, which
   * the endpoint does accept; there is still no equivalent for a *plan*, whose schema advertises
   * `edits` that `/resume` ignores (TD-5).
   */
  streamResume(
    sessionId: string,
    decision: HarnessDecision,
    message: string | undefined,
    steering: HarnessSteering | undefined,
    expectedInterruptId: string | undefined,
    context: HarnessMessageContext,
    allowAdjustments?: boolean,
    onAdmitted?: () => void,
    mergeAnswer?: HarnessMergeAnswer
  ): Observable<HarnessFrame> {
    const uiContext = decision === 'respond' ? context.uiContext : undefined

    return this.#stream(
      `/sessions/${encodeURIComponent(sessionId)}/resume`,
      {
        decision,
        ...(expectedInterruptId && { expected_interrupt_id: expectedInterruptId }),
        ...(message && { message }),
        // Only sent when there is something to steer. Approval is never inferred from steering:
        // without `decision: "approve"` this resumes as a respond.
        ...(steering?.change_decisions?.length && { change_decisions: steering.change_decisions }),
        ...(steering?.add_out_of_scope?.length && { add_out_of_scope: steering.add_out_of_scope }),
        ...(steering?.answer_questions?.length && { answer_questions: steering.answer_questions }),
        context: turnContextBody(context.call, uiContext),
        // "Let the agent adjust within this plan" rides the approval it qualifies (D15).
        ...(decision === 'approve' &&
          allowAdjustments !== undefined && {
            approval_options: { allow_adjustments: allowAdjustments },
          }),
        ...mergeAnswer,
      },
      context.headers,
      onAdmitted
    )
  }

  /** The 401-then-not-ok ladder for the streaming path. Its 401 is the only one in the app that
   * `ErrorInterceptor` never sees, so it ends the designer session by hand — otherwise a stale
   * token would leave the maker staring at a conversation that can never resume. */
  async #assertOk(response: Response): Promise<void> {
    if (response.status === 401) {
      this.#logout()

      throw authError()
    }

    if (!response.ok) {
      throw httpError(await readError(response), response.status)
    }
  }

  #feedbackPath(sessionId: string, messageId: string): string {
    return `${this.#config.baseUrl()}/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/feedback`
  }

  #sessionPath(sessionId: string): string {
    return `${this.#config.baseUrl()}/sessions/${encodeURIComponent(sessionId)}`
  }

  /** `HttpClient` options naming the call's project or its owner-only scope. */
  #options(scope: RequestScope): { context: ReturnType<typeof requestScopeContext> } {
    return { context: requestScopeContext(scope) }
  }

  /** The version and branch the message-feedback clear endpoint requires. */
  #sessionQuery(): URLSearchParams {
    const { app_version_id, branch } = this.#config.sessionBody()

    return new URLSearchParams({ app_version_id, branch })
  }

  /** One non-streaming call, as a promise the store can await.
   *
   * `parse` rather than a cast: `HttpClient` checks the *status*, and a 200 carrying somebody
   * else's JSON used to become a typed object that lies about itself. Each shape brings its own
   * guard from `harness.types.ts`, so the check sits beside the type it produces. The failures
   * keep the same codes the streaming path uses, because the store reads codes, not transports. */
  async #read<T>(call: Observable<unknown>, parse: (value: unknown) => T | null): Promise<T> {
    let body: unknown

    try {
      body = await firstValueFrom(call)
    } catch (error) {
      throw fromHttpFailure(error)
    }

    const parsed = parse(body)

    if (!parsed) {
      throw malformedResponseError()
    }

    return parsed
  }

  /** One SSE stream. Completes only after an `end` frame has been seen; a body that runs out
   * without one errors with `stream_truncated`, because the contract makes `end` mandatory and
   * completing normally would render as a turn that finished with nothing to show. */
  #stream(
    path: string,
    body: Record<string, unknown> | undefined,
    headers: Record<string, string>,
    onAdmitted?: () => void
  ): Observable<HarnessFrame> {
    return new Observable<HarnessFrame>((subscriber) => {
      const abort = new AbortController()

      const run = async (): Promise<void> => {
        // A transport failure rejects with the browser's own words ("network error", "Failed to
        // fetch"), which say nothing to the maker and carry no code: it is a lost connection.
        const lost = (error: unknown): never => {
          throw abort.signal.aborted ? error : truncatedStreamError(LOST_CONNECTION)
        }
        const response = await fetch(`${this.#config.baseUrl()}${path}`, {
          method: body ? 'POST' : 'GET',
          headers: { ...headers, Accept: 'text/event-stream' },
          ...(body && { body: JSON.stringify(body) }),
          signal: abort.signal,
        }).catch(lost)

        await this.#assertOk(response)
        onAdmitted?.()

        if (!response.body) {
          throw new Error('The harness returned no response body.')
        }

        const reader = response.body.getReader()
        const parser = new SseFrameParser()
        let sawEnd = false

        // A `for await` over the reader would be tidier, but ReadableStream is not reliably
        // async-iterable across the browsers this app supports.
        for (;;) {
          // Cut after `end`, the stream had already said all it had to.
          const { done, value } = await reader
            .read()
            .catch((error: unknown) => (sawEnd ? { done: true, value: undefined } : lost(error)))

          if (done) {
            break
          }

          const frames = parser
            .push(value)
            .map((event) => toFrame(event.event, event.data))
            .filter((frame): frame is HarnessFrame => !!frame)

          frames.forEach((frame) => {
            sawEnd = sawEnd || frame.type === 'end'
            subscriber.next(frame)
          })
        }

        parser.end()

        if (!sawEnd) {
          throw truncatedStreamError()
        }

        subscriber.complete()
      }

      run().catch((error: unknown) => {
        // An abort is this observable being torn down, not a failure to report.
        if (abort.signal.aborted) {
          return
        }

        subscriber.error(error)
      })

      return () => abort.abort()
    })
  }
}

/** The pilot's rename kept its original path; every later operation is addressed by id under
 * `operations/`. One function so the preview and the run cannot disagree. */
/** The whole-plan endpoints all hang off one prefix, the way a single operation's hang off
 * `executionPath`. Named so `run`, `preview` and `pause` cannot drift apart. */
const PLAN_EXECUTION_PATH = 'execution/plan'

function executionPath(operationId: string): string {
  return operationId === DEFAULT_OPERATION_ID
    ? 'execution'
    : `execution/operations/${encodeURIComponent(operationId)}`
}

const LOST_CONNECTION = 'The connection to the harness was lost.'

function truncatedStreamError(
  message = 'The connection ended before the turn finished.'
): Error & { code: string } {
  return Object.assign(new Error(message), { code: HARNESS_ERROR_CODES.streamTruncated })
}

/** A 401 comes from ai-gateway, which rejects before the harness sees the request: the designer
 * session is not valid, rather than the turn having failed. Logout is already under way by the
 * time this is thrown; the code is what stops the UI offering a retry that cannot succeed. */
function authError(): Error & { code: string } {
  return Object.assign(new Error('Your session has expired. Log in again to continue.'), {
    code: HARNESS_ERROR_CODES.notAuthenticated,
  })
}

/** A 2xx whose body is not what the endpoint promises. Coded because the store reads an uncoded
 * error as `stream_truncated`, so without this a malformed body would reach the maker as "the
 * connection ended" — blaming the network for an answer the server gave. */
function malformedResponseError(): Error & { code: string } {
  return Object.assign(new Error('The harness returned an answer this client cannot read.'), {
    code: HARNESS_ERROR_CODES.malformedResponse,
  })
}

/** A non-2xx answer, carrying what the harness said. Coded so the store does not mistake it for a
 * cut stream: a 502 from the relay, or the harness's own "Fx-Organization-Id is required", used to
 * reach the maker as "the connection ended". The status rides along, because a 409 is a refusal
 * the maker can act on and a 5xx is not.
 *
 * A structured refusal — `{"detail": {"code": "target_headers_required", …}}` — also keeps its `code` as
 * `reason` and the whole detail beside it, so the store can act on the refusal rather than parse
 * its sentence. */
function httpError(said: SaidError, status: number): HarnessHttpError {
  return Object.assign(new Error(said.message), {
    code: HARNESS_ERROR_CODES.httpError,
    status,
    ...(said.detail && { reason: asString(said.detail['code']), detail: said.detail }),
  })
}

/** What a refused call carries. `reason` and `detail` only on a structured refusal. */
export type HarnessHttpError = Error & {
  code: string
  status: number
  reason?: string
  detail?: Record<string, unknown>
}

/**
 * An `HttpClient` failure, in the vocabulary the streaming path uses.
 *
 * A 2xx that reaches the error channel is Angular failing to parse the body, not the server
 * failing, so it reads as a malformed response rather than an HTTP error. `ErrorInterceptor` has
 * already called `logout()` for a 401; the code only decides what the UI says meanwhile.
 */
function fromHttpFailure(error: unknown): Error & { code: string } {
  // Anything that is not an `HttpErrorResponse` never reached the wire — a guard throwing, a
  // completed-empty stream. There is no status to report, so it reads as an unusable answer.
  if (!(error instanceof HttpErrorResponse)) {
    return malformedResponseError()
  }

  if (error.status === 401) {
    return authError()
  }

  if (error.status >= 200 && error.status < 300) {
    return malformedResponseError()
  }

  return httpError(
    saidError(error.error, `The harness returned ${error.status} ${error.statusText}.`),
    error.status
  )
}

/** The same, one transport down: raw `fetch` hands back an unread body, so the JSON has to be
 * parsed before `saidError` can look inside it. */
async function readError(response: Response): Promise<SaidError> {
  const fallback = `The harness returned ${response.status} ${response.statusText}.`

  try {
    return saidError(await response.json(), fallback)
  } catch {
    return { message: fallback }
  }
}

type SaidError = { message: string; detail?: Record<string, unknown> }

/** What the harness said went wrong, from a body either transport handed over: a missing header
 * as `400 {"error": "…"}`, every lifecycle refusal as FastAPI's `409`/`422 {"detail": "…"}`. Both
 * say exactly what is wrong, so both are read before giving up on the body. A structured `detail`
 * carrying its own `message` is read by it; any other is shown as its JSON rather than as
 * `[object Object]`. Shared so a refusal reads the same whether it arrived on a read or a stream. */
function saidError(body: unknown, fallback: string): SaidError {
  const said = isRecord(body) ? (body['error'] ?? body['detail']) : undefined
  const message = asString(said)

  if (message) {
    return { message }
  }

  if (isRecord(said)) {
    return { message: asString(said['message']) || JSON.stringify(said), detail: said }
  }

  return { message: said != null ? JSON.stringify(said) : fallback }
}

/** Conversations per page: a panel's worth, with room for the grouping to show more than today. */
const CONVERSATION_PAGE = 50

/** The turn's `context{…}` — where the maker is, with what they have open. */
export function turnContextBody(
  call: HarnessCallScope,
  uiContext: HarnessUiContext | undefined
): Record<string, unknown> {
  return { ...call.context, ...(uiContext && { ui_context: uiContext }) }
}
